import type { CarSpec, DrivetrainSpec, VisualTuning } from './types.js';

const tuning = (
  paint: string,
  accent: string,
  rims: string,
  spoiler: VisualTuning['spoiler'],
  wheels: VisualTuning['wheels'],
): VisualTuning => ({
  paint,
  accent,
  rims,
  neon: '#45f5ff',
  neonEnabled: false,
  spoiler,
  wheels,
  decal: 'none',
  stance: 0,
});

const drivetrain = (
  gearCount: number,
  idleRpm: number,
  redlineRpm: number,
  shiftRpm: number,
  engineBraking: number,
  shiftTime: number,
): DrivetrainSpec => ({ gearCount, idleRpm, redlineRpm, shiftRpm, engineBraking, shiftTime });

export const CARS: readonly CarSpec[] = [
  {
    id: 'vortex-r',
    name: 'Vortex R',
    className: 'Street / Balanced',
    description: 'Predictable grip and a broad power band. The perfect all-rounder.',
    topSpeed: 67,
    acceleration: 9.2,
    handling: 2.25,
    braking: 15,
    offroadGrip: 0.45,
    mass: 1420,
    body: 'coupe',
    drivetrain: drivetrain(6, 900, 7_200, 6_750, 1.05, 0.13),
    dimensions: { width: 1.88, length: 4.45, height: 1.24, wheelBase: 2.66 },
    defaultTuning: tuning('#dbe3e8', '#ff3b2f', '#5d6772', 'ducktail', 'five-spoke'),
  },
  {
    id: 'apex-gt',
    name: 'Apex GT',
    className: 'Exotic / Velocity',
    description: 'Low, planted and brutally fast once the road opens up.',
    topSpeed: 76,
    acceleration: 10.4,
    handling: 2.12,
    braking: 16.5,
    offroadGrip: 0.3,
    mass: 1310,
    body: 'supercar',
    drivetrain: drivetrain(7, 1_000, 8_500, 8_050, 0.82, 0.095),
    dimensions: { width: 1.96, length: 4.62, height: 1.12, wheelBase: 2.72 },
    defaultTuning: tuning('#ff5a1f', '#17191d', '#6a717a', 'gt', 'turbine'),
  },
  {
    id: 'tempest-x',
    name: 'Tempest X',
    className: 'Roadster / Sprint',
    description: 'Lightweight response and explosive acceleration through technical sectors.',
    topSpeed: 63,
    acceleration: 11.6,
    handling: 2.55,
    braking: 17,
    offroadGrip: 0.38,
    mass: 1080,
    body: 'roadster',
    drivetrain: drivetrain(6, 950, 7_800, 7_350, 0.92, 0.105),
    dimensions: { width: 1.82, length: 4.12, height: 1.15, wheelBase: 2.48 },
    defaultTuning: tuning('#35c9b6', '#0c1718', '#c7d2d5', 'none', 'mesh'),
  },
  {
    id: 'nightfall-rs',
    name: 'Nightfall RS',
    className: 'Track / Precision',
    description: 'Aero-led precision for drivers who refuse to lift.',
    topSpeed: 71,
    acceleration: 10,
    handling: 2.72,
    braking: 18,
    offroadGrip: 0.34,
    mass: 1250,
    body: 'tourer',
    drivetrain: drivetrain(7, 1_050, 8_200, 7_750, 0.88, 0.09),
    dimensions: { width: 1.91, length: 4.52, height: 1.2, wheelBase: 2.7 },
    defaultTuning: tuning('#20242b', '#a9ff2f', '#4a5058', 'gt', 'mesh'),
  },
  {
    id: 'titan-xr',
    name: 'Titan XR',
    className: 'Muscle / Power',
    description: 'Heavyweight torque, long slides and an unmistakable silhouette.',
    topSpeed: 69,
    acceleration: 9.8,
    handling: 1.92,
    braking: 14,
    offroadGrip: 0.48,
    mass: 1680,
    body: 'muscle',
    drivetrain: drivetrain(6, 760, 6_700, 6_150, 1.28, 0.16),
    dimensions: { width: 1.94, length: 4.78, height: 1.3, wheelBase: 2.78 },
    defaultTuning: tuning('#6829ff', '#101014', '#a3aab3', 'ducktail', 'five-spoke'),
  },
  {
    id: 'mammoth-6x6',
    name: 'Mammoth 6×6',
    className: 'Heavy / Off-road',
    description: 'Six driven wheels, huge suspension travel and the confidence to take the rough line.',
    topSpeed: 58,
    acceleration: 8,
    handling: 1.72,
    braking: 13,
    offroadGrip: 0.92,
    mass: 2450,
    body: 'suv',
    drivetrain: drivetrain(6, 700, 6_100, 5_650, 1.18, 0.17),
    dimensions: { width: 2.16, length: 5.18, height: 1.82, wheelBase: 3.35 },
    defaultTuning: tuning('#d6a62e', '#171a1c', '#454a52', 'none', 'mesh'),
  },
  {
    id: 'juggernaut-rig',
    name: 'Juggernaut Rig',
    className: 'Heavy / Big rig',
    description: 'A stripped racing hauler with towering torque and enough mass to own every lane.',
    topSpeed: 54,
    acceleration: 6.7,
    handling: 1.32,
    braking: 11,
    offroadGrip: 0.72,
    mass: 5200,
    body: 'truck',
    drivetrain: drivetrain(8, 580, 4_300, 3_950, 0.86, 0.22),
    dimensions: { width: 2.46, length: 6.75, height: 2.62, wheelBase: 4.35 },
    defaultTuning: tuning('#e8ecef', '#ff3b30', '#bcc3c9', 'none', 'five-spoke'),
  },
  {
    id: 'ironclad-t9',
    name: 'Ironclad T-9',
    className: 'Tracked / Combat',
    description: 'A working battle tank: rotate the turret with Q/E and fire its cannon with F.',
    topSpeed: 32,
    acceleration: 5.4,
    handling: 1.55,
    braking: 10,
    offroadGrip: 1,
    mass: 9000,
    body: 'tank',
    drivetrain: drivetrain(5, 520, 3_200, 2_850, 0.68, 0.24),
    dimensions: { width: 2.86, length: 5.72, height: 2.05, wheelBase: 3.5 },
    defaultTuning: tuning('#566449', '#252d22', '#1b1d19', 'none', 'turbine'),
  },
  {
    id: 'volt-r9',
    name: 'Volt R9',
    className: 'Superbike / Velocity',
    description: 'A featherweight superbike with explosive drive, precise braking and a committed racing lean.',
    topSpeed: 78,
    acceleration: 13.2,
    handling: 2.7,
    braking: 19,
    offroadGrip: 0.28,
    mass: 235,
    body: 'bike',
    drivetrain: drivetrain(6, 1_450, 13_500, 12_750, 1.05, 0.07),
    dimensions: { width: 0.82, length: 2.14, height: 1.2, wheelBase: 1.44 },
    defaultTuning: tuning('#f7f9fb', '#ff2f45', '#41464e', 'none', 'turbine'),
  },
  {
    id: 'nomad-xr',
    name: 'Nomad XR',
    className: 'Scrambler / All-terrain',
    description: 'Long-travel suspension and knobbly tyres make sand and rough shortcuts part of the racing line.',
    topSpeed: 59,
    acceleration: 11.1,
    handling: 2.5,
    braking: 16.5,
    offroadGrip: 0.88,
    mass: 198,
    body: 'bike',
    drivetrain: drivetrain(6, 1_200, 9_800, 9_150, 1.22, 0.085),
    dimensions: { width: 0.88, length: 2.2, height: 1.3, wheelBase: 1.49 },
    defaultTuning: tuning('#d9b52c', '#101519', '#d5dce0', 'none', 'five-spoke'),
  },
  {
    id: 'wiener-wagon',
    name: 'Wiener Wagon',
    className: 'Novelty / Snack Attack',
    description: 'A grilled frank in a toasted bun with V8 relish. Heavy on the mustard, heavier on the slides.',
    topSpeed: 66,
    acceleration: 9.6,
    handling: 1.98,
    braking: 14.5,
    offroadGrip: 0.46,
    mass: 1520,
    body: 'muscle',
    novelty: 'hotdog',
    drivetrain: drivetrain(6, 780, 6_600, 6_100, 1.2, 0.15),
    dimensions: { width: 1.92, length: 4.96, height: 1.45, wheelBase: 2.85 },
    defaultTuning: tuning('#d68a3c', '#c4482c', '#d9dee3', 'none', 'five-spoke'),
  },
  {
    id: 'quackster',
    name: 'Quackster',
    className: 'Novelty / Bath Time',
    description: 'A featherweight rubber duck with a big smile, a bobbing head and surprising bite through the bends.',
    topSpeed: 62,
    acceleration: 11.2,
    handling: 2.5,
    braking: 16.5,
    offroadGrip: 0.4,
    mass: 1040,
    body: 'roadster',
    novelty: 'duck',
    drivetrain: drivetrain(5, 1_000, 7_600, 7_150, 0.95, 0.11),
    dimensions: { width: 1.86, length: 3.9, height: 1.6, wheelBase: 2.3 },
    defaultTuning: tuning('#ffc81a', '#ff7a1a', '#f4f6f8', 'none', 'five-spoke'),
  },
  {
    id: 'couch-rocket',
    name: 'Couch Rocket',
    className: 'Novelty / Living Room',
    description: 'A tufted velvet Chesterfield on a go-kart frame. Table lamps for headlights, popcorn included.',
    topSpeed: 64,
    acceleration: 10,
    handling: 2.3,
    braking: 15.5,
    offroadGrip: 0.42,
    mass: 1180,
    body: 'coupe',
    novelty: 'sofa',
    drivetrain: drivetrain(6, 850, 7_000, 6_550, 1, 0.12),
    dimensions: { width: 2.1, length: 3.4, height: 1.6, wheelBase: 2.2 },
    defaultTuning: tuning('#16584c', '#f0b23a', '#c9a45c', 'none', 'mesh'),
  },
  {
    id: 'bigfoot-bubble',
    name: 'Bigfoot Bubble',
    className: 'Novelty / Monster Micro',
    description: 'A two-tone bubble car perched on monster-truck tyres and long-travel coilovers. Kerbs are optional.',
    topSpeed: 57,
    acceleration: 8.6,
    handling: 1.78,
    braking: 13.5,
    offroadGrip: 0.94,
    mass: 2100,
    body: 'suv',
    novelty: 'bigfoot',
    drivetrain: drivetrain(5, 720, 6_000, 5_550, 1.15, 0.16),
    dimensions: { width: 2.16, length: 3.7, height: 2.75, wheelBase: 2.3 },
    defaultTuning: tuning('#e8352f', '#f3ead2', '#d4dadf', 'none', 'mesh'),
  },
] as const;

export const CAR_BY_ID = new Map(CARS.map((car) => [car.id, car]));

export function cloneTuning(value: VisualTuning): VisualTuning {
  return { ...value };
}
